import { toRelativeUrl } from '@okta/okta-auth-js'
import { oktaAuth } from '../authentication/OktaSSO'
import localStorageService from './localStorageService'
import store from '../redux/store'
import { showErrorMessage } from '../redux/reducers/snackbarReducer'
import { BaseResponse } from '../types/base-response'
import { logDiagnosticEvent } from './diagnosticLogger'

const redirectNowMessage = 'Okta token has expired, the site will refresh now...'

// When the access token expires (~1h) several independent flows can fire an OIDC
// redirect at the same time: the tokenManager "expired" listener (LogoutHelper),
// ProtectedRoute (authState.isAuthenticated flips to false) and concurrent 401s
// raised by multiple axios interceptors. Each signInWithRedirect call mints a fresh
// PKCE code_verifier and overwrites the previous one in session storage, so by the
// time /login/callback runs the verifier no longer matches the code Okta returns
// and token exchange fails, leaving the user on a white screen.
//
// This in-flight guard guarantees only the first caller ever reaches
// signInWithRedirect; every subsequent caller (from any flow) becomes a no-op.
let redirectInFlight = false

const beginRedirect = (): boolean => {
  if (redirectInFlight || localStorageService.isOktaRedirecting()) {
    // BSSERP-2124: this path was previously silent, making it impossible to
    // tell from browser console/logs whether a stuck guard was blocking
    // re-auth. The guard itself still has a TTL (see localStorageService)
    // so it cannot stay blocked forever, but every block should be visible.
    // eslint-disable-next-line no-console
    console.warn('[oktaService] Okta redirect blocked by isRedirecting guard')
    logDiagnosticEvent({
      eventType: 'redirect_blocked_by_guard',
      outcome: 'blocked',
      level: 'warn',
    })
    return false
  }
  redirectInFlight = true
  localStorageService.setIsOktaRedirecting(true)
  return true
}

const releaseRedirectGuard = () => {
  redirectInFlight = false
  localStorageService.setIsOktaRedirecting(false)
}

const executeRedirect = async (originalUri: string) => {
  try {
    oktaAuth.setOriginalUri(originalUri)
    await oktaAuth.signInWithRedirect()
  } catch (refreshError) {
    releaseRedirectGuard()
    throw refreshError
  }
}

interface ValidationError extends BaseResponse {
  response: {
    data: BaseResponse
  }
}

const oktaService = {
  silentAcquisitionInProgress: false,

  // Single entry point for ANY caller that would otherwise invoke
  // oktaAuth.signInWithRedirect directly (ProtectedRoute, LogoutHelper, QR booking,
  // httpAuth 401/expired-token interceptors, etc.). Keeps the PKCE transaction
  // from being clobbered by competing flows.
  triggerOktaRedirect: async (originalUri?: string) => {
    if (!beginRedirect()) {
      return
    }
    const uri = originalUri ?? toRelativeUrl(window.location.href, window.location.origin)
    await executeRedirect(uri)
  },

  resyncOctaWithRedirect: async (delay?: number) => {
    if (!beginRedirect()) {
      return
    }

    const userId = store.getState().appSettings.employeeDetails.employeeId
    if (!userId) {
      releaseRedirectGuard()
      return
    }

    store.dispatch(showErrorMessage(redirectNowMessage))
    setTimeout(() => {
      const originalUri = toRelativeUrl(window.location.href, window.location.origin)
      executeRedirect(originalUri).catch(() => {
        /* guard already released inside executeRedirect */
      })
    }, delay ?? 3000)
  },

  tokenValidationError: (): ValidationError => ({
    errors: [],
    status: 401,
    response: {
      data: {
        errors: [],
        status: 401,
      },
    },
  }),

  // Exposed so callers that observe a successful Okta round-trip
  // (e.g. ProtectedRoute when authState.isAuthenticated becomes true)
  // can clear the in-flight guard. Without this, the localStorage
  // `isRedirecting` flag set in beginRedirect() is only ever cleared
  // on the error path, leaving it stuck as 'true' after a successful
  // redirect and silently no-op'ing all subsequent re-auth attempts.
  releaseRedirectGuard,
}

export default oktaService
